import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import type { ThreeCtx } from './useThreeScene';
import type { HdriPreset } from '../types';

const PALETTES: Record<
  HdriPreset,
  { skyTop: string; skyBottom: string; floor: string; glow: string }
> = {
  neutral: { skyTop: '#dbe8f4', skyBottom: '#ebefe8', floor: '#cbc4b8', glow: '#fff6ea' },
  warm: { skyTop: '#f8ddc8', skyBottom: '#f6efe6', floor: '#d8c3ab', glow: '#ffe9c8' },
  cool: { skyTop: '#cfe5fb', skyBottom: '#e2eef8', floor: '#bec8d3', glow: '#e6f4ff' },
};

/**
 * Neutral Viewer background for the technical scene.  It is deliberately not
 * an HDRI: materials still receive light from the selected procedural map,
 * while the operator sees a quiet, high-contrast studio rather than WebGL's
 * default black clear colour.  The background is deterministic so captures of
 * the same model retain a stable visual context.
 */
function buildStudioBackdrop(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 2048;
  c.height = 1024;
  const ctx = c.getContext('2d')!;

  const base = ctx.createLinearGradient(0, 0, 0, c.height);
  base.addColorStop(0, '#101722');
  base.addColorStop(0.48, '#172131');
  base.addColorStop(0.72, '#202b36');
  base.addColorStop(1, '#121923');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, c.width, c.height);

  const coolHalo = ctx.createRadialGradient(c.width * 0.28, c.height * 0.14, 0, c.width * 0.28, c.height * 0.14, c.width * 0.7);
  coolHalo.addColorStop(0, 'rgba(80, 128, 170, 0.20)');
  coolHalo.addColorStop(0.56, 'rgba(45, 78, 112, 0.07)');
  coolHalo.addColorStop(1, 'rgba(24, 38, 52, 0)');
  ctx.fillStyle = coolHalo;
  ctx.fillRect(0, 0, c.width, c.height);

  const warmHorizon = ctx.createRadialGradient(c.width * 0.74, c.height * 0.64, 0, c.width * 0.74, c.height * 0.64, c.width * 0.58);
  warmHorizon.addColorStop(0, 'rgba(179, 141, 93, 0.13)');
  warmHorizon.addColorStop(0.58, 'rgba(105, 88, 70, 0.04)');
  warmHorizon.addColorStop(1, 'rgba(26, 36, 47, 0)');
  ctx.fillStyle = warmHorizon;
  ctx.fillRect(0, 0, c.width, c.height);

  const t = new THREE.CanvasTexture(c);
  t.mapping = THREE.EquirectangularReflectionMapping;
  (t as any).colorSpace = (THREE as any).SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

function buildProceduralHdri(preset: HdriPreset): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 2048;
  c.height = 1024;
  const ctx = c.getContext('2d')!;
  const colors = PALETTES[preset];
  const grad = ctx.createLinearGradient(0, 0, 0, c.height);
  grad.addColorStop(0, colors.skyTop);
  grad.addColorStop(0.55, colors.skyBottom);
  grad.addColorStop(1, colors.floor);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, c.width, c.height);
  // Soft glow with deterministic placement.  The same selected map must have
  // the same pixels after reopen; random sky lights made render provenance
  // meaningless even though the selected preset had been persisted.
  for (let i = 0; i < 40; i += 1) {
    const x = ((i * 487 + 271) % c.width);
    const y = ((i * 191 + 97) % Math.round(c.height * 0.55));
    const r = 120 + ((i * 73) % 220);
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, hexA(colors.glow, 0.22 + ((i % 7) * 0.025)));
    g.addColorStop(1, hexA(colors.glow, 0));
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, c.width, c.height);
  }
  const t = new THREE.CanvasTexture(c);
  t.mapping = THREE.EquirectangularReflectionMapping;
  (t as any).colorSpace = (THREE as any).SRGBColorSpace;
  t.needsUpdate = true;
  return t;
}

function hexA(hex: string, a: number): string {
  const c = new THREE.Color(hex);
  return `rgba(${Math.round(c.r * 255)},${Math.round(c.g * 255)},${Math.round(c.b * 255)},${a})`;
}

export function useHdri(
  ctx: ThreeCtx | null,
  preset: HdriPreset,
  rotationDeg: number,
  intensity: number,
  showBackground: boolean,
) {
  const cacheRef = useRef<Partial<Record<HdriPreset, THREE.Texture>>>({});
  const backdropRef = useRef<THREE.Texture | null>(null);

  useEffect(() => {
    if (!ctx) return;
    let cancelled = false;

    const apply = (tex: THREE.Texture) => {
      if (cancelled || !ctx) return;
      ctx.scene.environment = tex;
      const rot = (rotationDeg * Math.PI) / 180;
      const sceneAny = ctx.scene as unknown as {
        environmentRotation?: { set(x: number, y: number, z: number): void };
        backgroundRotation?: { set(x: number, y: number, z: number): void };
      };
      sceneAny.environmentRotation?.set(0, rot, 0);
      sceneAny.backgroundRotation?.set(0, rot, 0);
      (sceneAny as { environmentIntensity?: number }).environmentIntensity = intensity;
      // The neutral Viewer backdrop belongs to the DOM layer behind this
      // transparent canvas. HDRI remains an explicit visualisation option;
      // it must not replace the scene with a second synthetic room by default.
      ctx.scene.background = showBackground ? tex : null;
    };

    if (cacheRef.current[preset]) {
      apply(cacheRef.current[preset]!);
      return () => {
        cancelled = true;
      };
    }

    // This release ships procedural studio maps instead of binary .hdr assets.
    // Referencing missing /hdri/*.hdr files makes RGBELoader parse the SPA
    // fallback HTML and throw before the normal onError path runs.
    const proc = buildProceduralHdri(preset);
    cacheRef.current[preset] = proc;
    apply(proc);

    return () => {
      cancelled = true;
    };
  }, [ctx, preset, rotationDeg, intensity, showBackground]);
}
